import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseWidget, NgCompInputs } from 'gridstack/dist/angular';

import { ANALYZING_MODES, WIDGET_CATEGORIES } from '@shared/constants';
import {
  CommonResponseDTO,
  ILineWidgetData,
  ILineWidgetResponse,
  IWidget,
} from '@shared/interfaces';

import { PermissionFiltrationService } from '../../../../../services/permission-filtration.service';
import {
  LoggedUserService,
  UserInfoResponseDTO,
} from '../../../../auth/services';
import { WidgetApiService } from '../../../services/widgets.api.service';

import { SnackbarService } from './../../../../../services/snackbar.service';

@Component({
  selector: 'app-line-chart',
  templateUrl: 'line-chart.component.html',
  styleUrls: ['line-chart.component.scss'],
  standalone: false,
})
export class LineChartComponent
  extends BaseWidget
  implements OnChanges, OnInit
{
  @Input() widget: IWidget;
  @Input() clickable: boolean;
  @Input() closable: boolean;

  multi: ILineWidgetData[];
  description: string;

  animations = true;
  xAxis = true;
  yAxis = true;
  scheme = 'cool';

  loggedUserInfo: UserInfoResponseDTO;
  hasPermissions = false;
  isLoading = false;
  isFailedToLoad = false;

  constructor(
    private snackbar: SnackbarService,
    private router: Router,
    private translate: TranslateService,
    private widgetApiService: WidgetApiService,
    private permissionFiltrationService: PermissionFiltrationService,
    private loggedUserService: LoggedUserService
  ) {
    super();
  }

  public override serialize(): NgCompInputs | undefined {
    return this.widget ? { text: this.widget } : undefined;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loggedUserService.dataStore.subscribe((data) => {
      this.loggedUserInfo = data;
      if (data) {
        this.refreshWidget();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widget?.currentValue.endpoint) {
      this.isLoading = true;
      this.refreshWidget();
    }
  }

  onClickWidget() {
    switch (this.widget.category_key) {
      case WIDGET_CATEGORIES.LOGS:
        this.router.navigate([`/app/logs`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.CONNECTED_APPS:
        this.router.navigate([`/app/apps`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.CONNECTIONS:
        this.router.navigate([`/app/connections`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.DRIVES:
        this.router.navigate([`app/storage/drives`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.IDENTITIES:
        this.router.navigate([`/app/identities`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.KNOWLEDGEBASE:
        this.router.navigate([`/app/knowledgebase`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.MESSAGES:
        this.router.navigate([`/app/messages`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.MODULES:
        this.router.navigate([`/app/modules`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.NOTICES:
        this.router.navigate([`/app/notices`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.PERMISSIONS:
        this.router.navigate([`/app/authorization/permissions`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.PRIVILEGES:
        this.router.navigate([`/app/authorization/privileges`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.ROLES:
        this.router.navigate([`/app/authorization/roles`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.ACCOUNTS:
        this.router.navigate([`/app/accounts`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.AGREEMENTS:
        this.router.navigate([`/app/agreements`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.ASSESSMENTS:
        this.router.navigate([`/app/assessments`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.ASSESSORS:
        this.router.navigate([`/app/assessors`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.BLACKLIST:
        this.router.navigate([`/app/blacklist`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.BRANCHES:
        this.router.navigate([`/app/branches`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.CHART_OF_ACCOUNTS:
        this.router.navigate([`/app/chart-of-accounts`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.CLAIMS:
        this.router.navigate([`/app/claims`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.CONTROLS:
        this.router.navigate([`/app/controls`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.COVER_NOTES:
        this.router.navigate([`/app/cover-notes`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.CUSTOMERS:
        this.router.navigate([`/app/customers`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.ENDORSEMENTS:
        this.router.navigate([`/app/endorsements`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.GATEWAYS:
        this.router.navigate([`/app/gateways`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.INCIDENTS:
        this.router.navigate([`/app/incidents`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.INVOICES:
        this.router.navigate([`/app/invoices`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.MAPS:
        this.router.navigate([`/app/maps`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.PARTNERS:
        this.router.navigate([`/app/partners`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.PAYMENTS:
        this.router.navigate([`/app/payments`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.POLICIES:
        this.router.navigate([`/app/policies`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.PRODUCTS:
        this.router.navigate([`/app/products`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.TRANSACTIONS:
        this.router.navigate([`/app/transactions`], {
          queryParams: { isSearch: true },
        });
        break;

      case WIDGET_CATEGORIES.REINSURERS:
        this.router.navigate([`/app/reinsurers`], {
          queryParams: { isSearch: true },
        });
        break;

      default:
        this.router.navigate([`/app/launchpad`]);
        break;
    }
  }

  refreshWidget() {
    this.hasPermissions = this.permissionFiltrationService.validatePermissions({
      analyzingMode: ANALYZING_MODES.EVERY,
      permissions: this.widget.permissions,
    });
    this.isLoading = true;
    this.widgetApiService.getWidgetData(this.widget.endpoint).subscribe({
      next: (res: CommonResponseDTO<ILineWidgetResponse>) => {
        this.multi = res.data.data;
        this.description = res.data.description;
        this.isFailedToLoad = false;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.isFailedToLoad = true;
        this.snackbar.error(
          this.translate.instant('widgets.count-widget.failed-to-load-widget')
        );
      },
    });
  }
}
